import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import LinkOffIcon from '@mui/icons-material/LinkOff';

const SoloSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    utr: '',
    nino: '',
    vatNumber: '',
    businessName: ''
  });

  // Business Structure is a separate concept from tax registration details above:
  // it lives on CompanyInfo (via /company/profile), not UserTaxDetails, so it gets
  // its own state and its own save action rather than being folded into formData.
  const [legalStructure, setLegalStructure] = useState('');
  const [structureSaving, setStructureSaving] = useState(false);
  const [structureError, setStructureError] = useState(null);
  const [structureSuccess, setStructureSuccess] = useState(null);

  // HMRC ITSA connection state — separate from the tax details form above.
  // Reads from /solo/hmrc-itsa/status (backed by UserTaxDetails on the
  // server), and triggers the OAuth flow via /solo/hmrc-itsa/connect.
  const [hmrcStatus, setHmrcStatus] = useState(null);
  const [hmrcLoading, setHmrcLoading] = useState(true);
  const [hmrcConnecting, setHmrcConnecting] = useState(false);
  const [hmrcError, setHmrcError] = useState(null);
  const [hmrcNotice, setHmrcNotice] = useState(null);

  // Temporary test harness for the Obligations endpoint — reuses the
  // already-authenticated `api` client so there's no need for Postman or
  // manually copying JWTs into curl. Doubles as a first cut of the real
  // "obligations list view" flagged as still-needed in the ITSA setup
  // notes, not purely throwaway scaffolding.
  const [obligationsLoading, setObligationsLoading] = useState(false);
  const [obligationsResult, setObligationsResult] = useState(null);
  const [obligationsError, setObligationsError] = useState(null);

  useEffect(() => {
    fetchTaxDetails();
    fetchCompanyProfile();
    fetchHmrcStatus();
    handleOAuthRedirectParams();
  }, []);

  // The OAuth callback (routes/solo_hmrc_routes.py) redirects back here with
  // ?success=true or ?error=<code> in the URL. Surface that as a one-time
  // notice, then clean the URL so refreshing the page doesn't re-show it.
  const handleOAuthRedirectParams = () => {
    const params = new URLSearchParams(window.location.search);
    const successParam = params.get('success');
    const errorParam = params.get('error');

    if (successParam === 'true') {
      setHmrcNotice({ severity: 'success', message: 'Connected to HMRC successfully.' });
      // Fetch the Business ID now, from a normal frontend request that
      // carries the X-FP-* device headers. Deliberately not done in the
      // OAuth callback on the server — see solo_hmrc_routes.py.
      api.get('/solo/hmrc-itsa/business-details')
        .then(() => fetchHmrcStatus())
        .catch((err) => console.error('Business details fetch after connect failed:', err));
    } else if (errorParam) {
      const messages = {
        invalid_state: 'The connection attempt could not be verified. Please try again.',
        session_expired: 'Your session expired during the connection process. Please try again.',
        token_exchange_failed: 'HMRC could not complete the connection. Please try again.',
        callback_failed: 'Something went wrong connecting to HMRC. Please try again.',
      };
      setHmrcNotice({
        severity: 'error',
        message: messages[errorParam] || `HMRC connection failed (${errorParam}). Please try again.`
      });
    }

    if (successParam || errorParam) {
      // Strip the query params so a page refresh doesn't re-trigger the notice
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const fetchTaxDetails = async () => {
    try {
      const response = await api.get('/solo/settings/tax-details');
      setFormData({
        utr: response.data.utr || '',
        nino: response.data.nino || '',
        vatNumber: response.data.vatNumber || '',
        businessName: response.data.businessName || ''
      });
    } catch (err) {
      console.error('Error fetching tax details:', err);
      setError('Failed to load tax details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyProfile = async () => {
    try {
      const response = await api.get('/company/profile');
      setLegalStructure(response.data.legalStructure || '');
    } catch (err) {
      console.error('Error fetching company profile:', err);
      // Non-fatal: leave the selector unset rather than blocking the whole page load
    }
  };

  const fetchHmrcStatus = async () => {
    try {
      const response = await api.get('/solo/hmrc-itsa/status');
      setHmrcStatus(response.data);
    } catch (err) {
      console.error('Error fetching HMRC ITSA status:', err);
      // Non-fatal: treat as not connected rather than blocking the page
      setHmrcStatus({ connected: false, status: 'not_connected' });
    } finally {
      setHmrcLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value.toUpperCase()  // Store in uppercase for consistency
    }));
    setError(null);
    setSuccess(null);
  };

  const handleStructureChange = (e) => {
    setLegalStructure(e.target.value);
    setStructureError(null);
    setStructureSuccess(null);
  };

  const handleStructureSubmit = async (e) => {
    e.preventDefault();
    setStructureSaving(true);
    setStructureError(null);
    setStructureSuccess(null);

    try {
      await api.put('/company/profile', { legalStructure });
      setStructureSuccess('Business structure updated successfully!');

      setTimeout(() => {
        setStructureSuccess(null);
      }, 3000);

    } catch (err) {
      console.error('Error updating business structure:', err);
      setStructureError(err.response?.data?.error || 'Failed to update business structure');
    } finally {
      setStructureSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await api.put('/solo/settings/tax-details', formData);
      setSuccess('Tax details updated successfully!');

      // NINO may have just been saved for the first time — refresh HMRC
      // status so the Connect button's disabled state updates immediately
      // without requiring a page reload.
      fetchHmrcStatus();

      setTimeout(() => {
        setSuccess(null);
      }, 3000);

    } catch (err) {
      console.error('Error updating tax details:', err);
      setError(err.response?.data?.error || 'Failed to update tax details');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectHmrc = async () => {
    setHmrcConnecting(true);
    setHmrcError(null);

    try {
      const response = await api.post('/solo/hmrc-itsa/connect');
      // Full-page redirect to HMRC's OAuth authorisation screen — not an
      // in-app navigation, since the user needs to leave the app to log
      // into HMRC's own Government Gateway login.
      window.location.href = response.data.auth_url;
    } catch (err) {
      console.error('Error initiating HMRC ITSA connection:', err);
      const errData = err.response?.data;
      if (errData?.error === 'MISSING_NINO') {
        setHmrcError(errData.message || 'Add your National Insurance Number below before connecting.');
      } else {
        setHmrcError(errData?.error || 'Failed to start HMRC connection. Please try again.');
      }
      setHmrcConnecting(false);
    }
    // Deliberately not resetting hmrcConnecting in a finally block on the
    // success path — the browser is navigating away, so there's no "after"
    // state to return the button to.
  };

  const handleDisconnectHmrc = async () => {
    setHmrcConnecting(true);
    setHmrcError(null);

    try {
      await api.post('/solo/hmrc-itsa/disconnect');
      await fetchHmrcStatus();
      setHmrcNotice({ severity: 'success', message: 'Disconnected from HMRC.' });
    } catch (err) {
      console.error('Error disconnecting HMRC ITSA:', err);
      setHmrcError(err.response?.data?.error || 'Failed to disconnect. Please try again.');
    } finally {
      setHmrcConnecting(false);
    }
  };

  const handleFetchObligations = async () => {
    setObligationsLoading(true);
    setObligationsError(null);
    setObligationsResult(null);

    try {
      const response = await api.get('/solo/hmrc-itsa/obligations');
      setObligationsResult(response.data);
      // Refresh status too — a successful obligations fetch may have
      // triggered a fresh Business ID lookup if it wasn't already set.
      fetchHmrcStatus();
    } catch (err) {
      console.error('Error fetching ITSA obligations:', err);
      setObligationsError(err.response?.data || { error: err.message });
    } finally {
      setObligationsLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const isConnected = hmrcStatus?.connected;
  const nino_missing = !formData.nino;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/solo-dashboard')}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AccountBalanceIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Tax Settings
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Manage your tax registration details for HMRC reporting
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {hmrcNotice && (
          <Alert severity={hmrcNotice.severity} sx={{ mb: 3 }} onClose={() => setHmrcNotice(null)}>
            {hmrcNotice.message}
          </Alert>
        )}

        {/* Business Structure — separate concept and separate save action from the
            tax registration details below. This drives whether Self Assessment /
            Class 4 NI calculations apply at all, so it's kept distinct rather than
            bundled with UTR/NINO/VAT. */}
        <Box component="form" onSubmit={handleStructureSubmit} sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Business Structure
            </Typography>
          </Box>

          {structureError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {structureError}
            </Alert>
          )}

          {structureSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {structureSuccess}
            </Alert>
          )}

          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={7}>
              <FormControl fullWidth>
                <InputLabel id="legal-structure-label">Business Structure</InputLabel>
                <Select
                  labelId="legal-structure-label"
                  label="Business Structure"
                  name="legalStructure"
                  value={legalStructure}
                  onChange={handleStructureChange}
                >
                  <MenuItem value="SOLE_TRADER">Sole Trader</MenuItem>
                  <MenuItem value="LIMITED_COMPANY">Limited Company</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                This determines whether Self Assessment and Class 4 National Insurance apply to you.
                If you're not sure, check with your accountant.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Button
                type="submit"
                variant="contained"
                startIcon={structureSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={structureSaving || !legalStructure}
              >
                {structureSaving ? 'Saving...' : 'Save Business Structure'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Business Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Name"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                disabled
                helperText="Business name is managed by your account administrator"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Tax Registration Details
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unique Taxpayer Reference (UTR)"
                name="utr"
                value={formData.utr}
                onChange={handleChange}
                placeholder="1234567890"
                helperText="10-digit number from HMRC for Self Assessment"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="National Insurance Number (NINO)"
                name="nino"
                value={formData.nino}
                onChange={handleChange}
                placeholder="QQ123456C"
                helperText="Format: AA 12 34 56 A (spaces optional)"
                inputProps={{ maxLength: 13 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="VAT Registration Number (Optional)"
                name="vatNumber"
                value={formData.vatNumber}
                onChange={handleChange}
                placeholder="GB123456789"
                helperText="Only enter if you're VAT registered (turnover over £90k)"
                inputProps={{ maxLength: 12 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/solo-dashboard')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        <Divider sx={{ my: 4 }} />

        {/* HMRC Income Tax (MTD ITSA) connection — separate from the tax
            registration fields above. Reads UserTaxDetails.nino, so must
            be saved first (Connect button is disabled with an explanatory
            note until it is). */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CloudSyncIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              HMRC Income Tax Connection
            </Typography>
          </Box>

          {/* End of Year / Final Declaration disclosure — SL Solo only
              handles in-year quarterly updates. Shown regardless of
              connection status so it's visible whether or not HMRC is
              connected yet. */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              SL Solo currently supports <strong>in-year quarterly updates</strong> for Income Tax
              Self Assessment. Your End of Year declaration is a separate step, completed outside
              SL Solo until this feature is added.
            </Typography>
          </Alert>

          {hmrcError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {hmrcError}
            </Alert>
          )}

          <Card variant="outlined">
            <CardContent>
              {hmrcLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Checking connection status...
                  </Typography>
                </Box>
              ) : isConnected ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Connected to HMRC
                    </Typography>
                    <Chip label="Active" color="success" size="small" sx={{ ml: 1 }} />
                  </Box>
                  {hmrcStatus?.business_id && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Business ID: {hmrcStatus.business_id}
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={hmrcConnecting ? <CircularProgress size={16} /> : <LinkOffIcon />}
                    onClick={handleDisconnectHmrc}
                    disabled={hmrcConnecting}
                    sx={{ mr: 2 }}
                  >
                    {hmrcConnecting ? 'Disconnecting...' : 'Disconnect'}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={obligationsLoading ? <CircularProgress size={16} /> : null}
                    onClick={handleFetchObligations}
                    disabled={obligationsLoading}
                  >
                    {obligationsLoading ? 'Fetching...' : 'Fetch Obligations (test)'}
                  </Button>

                  {obligationsError && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'error.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                        Error:
                      </Typography>
                      <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {JSON.stringify(obligationsError, null, 2)}
                      </Typography>
                    </Box>
                  )}

                  {obligationsResult && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                        Result ({obligationsResult.open_count} open, {obligationsResult.fulfilled_count} fulfilled):
                      </Typography>
                      <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {JSON.stringify(obligationsResult, null, 2)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WarningIcon color="warning" fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Not connected
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {hmrcStatus?.status_message ||
                      'Connect to HMRC to enable Making Tax Digital Income Tax submissions.'}
                  </Typography>
                  {nino_missing && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Add and save your National Insurance Number above before connecting.
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    startIcon={hmrcConnecting ? <CircularProgress size={16} /> : <CloudSyncIcon />}
                    onClick={handleConnectHmrc}
                    disabled={hmrcConnecting || nino_missing}
                  >
                    {hmrcConnecting ? 'Connecting...' : 'Connect to HMRC'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'warning.lighter', borderRadius: 1, border: 1, borderColor: 'warning.light' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Where to find these numbers
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            <strong>UTR:</strong> Found on your Self Assessment registration letter from HMRC or on your tax return.
            <br />
            <strong>NINO:</strong> Found on your payslips, P60, or letters from HMRC/DWP.
            <br />
            <strong>VAT Number:</strong> Only needed if you're registered for VAT (turnover over £90,000).
            <br />
            <br />
            These details are required for Making Tax Digital (MTD) submissions to HMRC.
          </Typography>
        </Box>

        <Box sx={{ mt: 3, p: 3, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            🔐 Security & Privacy
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Your tax details are stored securely and encrypted. They will only be used for HMRC
            reporting and will never be shared with third parties. You can update or remove them at any time.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default SoloSettings;